import loadAllConversationItemParts from './loadAllConversationItemParts';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { ConversationItemParts, ConversationReadingPaneNode, mailStore } from 'owa-mail-store';
import { OOF_ITEM_CLASS_REGEX } from 'owa-mail-store/lib/utils/constants';
import Message from 'owa-service/lib/contract/Message';

interface ParentChildRelation {
    itemIds: string[]; // ItemIds associated with this node
    parentImId?: string; // May not have a parent internet message id if it is the root node
    childrenImIds: string[]; // nodes with no children will be set with empty array
}

/*
 * -------------------------------------------------------------
 * internetMessageId -> itemIds[], parentImId, childrenImId[]
 * -------------------------------------------------------------
 */
type ParentChildRelationMap = Map<string, ParentChildRelation>;

let relationMap: ParentChildRelationMap;
let currentLoadedConversationId: string;

/*
 * The current implementation is based on the assumption that we always have root node
 * available from GCI calls.
 */
async function getConversationItemsRelationMap(
    conversationId: string
): Promise<ParentChildRelationMap | null> {
    if (currentLoadedConversationId != conversationId) {
        currentLoadedConversationId = conversationId;
        await loadAllConversationItemParts(conversationId);

        const conversation = mailStore.conversations?.get(conversationId);

        if (!conversation) {
            return null;
        }

        const allConversationNodeIds: string[] | undefined = conversation?.conversationNodeIds;
        const conversationNodeIdCount = allConversationNodeIds?.length;
        if (!allConversationNodeIds || !conversationNodeIdCount || conversationNodeIdCount == 0) {
            return null;
        }
        const clonedConversationNodeIds = [...allConversationNodeIds];

        const isNewestOnBottom: boolean =
            conversation.conversationSortOrder === 'ChronologicalNewestOnBottom';

        // if sort order is newest on top, reverse the nodes in cloneConversationNodeIds to get
        // root node first and then child nodes in the order of messages sent.
        if (!isNewestOnBottom) {
            clonedConversationNodeIds.reverse();
        }
        const rootNode: ConversationReadingPaneNode | undefined = mailStore.conversationNodes.get(
            clonedConversationNodeIds[0]
        );
        // initialize map with root node
        const conversationItemsRelationMap: Map<string, ParentChildRelation> = new Map();
        if (rootNode) {
            conversationItemsRelationMap.set(rootNode.internetMessageId, {
                itemIds: rootNode.itemIds,
                childrenImIds: [],
            });
        }

        let index = 1; // skip root node
        while (index < conversationNodeIdCount) {
            const node: ConversationReadingPaneNode | undefined = mailStore.conversationNodes.get(
                clonedConversationNodeIds[index]
            );
            updateConversationRelationMapWithNode(conversationItemsRelationMap, node);
            index += 1;
        }

        relationMap = conversationItemsRelationMap;
    }

    return relationMap;
}

/**
 * Helper function to check if mailstore contains conversation and nodes
 */
export function isConversationInMailStore(conversationId: string): boolean {
    const conversationItemParts: ConversationItemParts | undefined = mailStore.conversations?.get(
        conversationId
    );
    if (!conversationItemParts) {
        return false;
    }
    const allConversationNodeIds: string[] = conversationItemParts.conversationNodeIds;
    const conversationNodeIdCount = allConversationNodeIds?.length;
    if (!allConversationNodeIds || !conversationNodeIdCount || conversationNodeIdCount == 0) {
        return false;
    }
    return true;
}

/**
 * This function is used to get the local leaf nodes from conversation tree
 * VSO: 108934 the parent folder id is needed in Monarch when mon-conv-useHxForConvListViewLoad
 *      since Hx currently only returns the latest local item
 */
export async function getLatestItemIdsInForks(
    conversationId: string,
    localItemIds: string[],
    parentFolderId?: string
): Promise<string[]> {
    const conversationRelationMap = await getConversationItemsRelationMap(conversationId);

    if (conversationRelationMap) {
        const latestItemIdsInForks: string[] = [];
        conversationRelationMap.forEach((relation, imId) => {
            // If it does not have any children then it is a leaf node
            if (relation.childrenImIds.length === 0) {
                let localItemId = checkForLocalItemIds(
                    localItemIds,
                    relation.itemIds,
                    parentFolderId
                );
                localItemId && latestItemIdsInForks.push(localItemId);
                let currentRelation: ParentChildRelation | undefined = relation;
                // If the latest local item id is not in the first node, look at the parents
                while (!localItemId && currentRelation?.parentImId) {
                    currentRelation = conversationRelationMap.get(currentRelation?.parentImId);
                    localItemId = checkForLocalItemIds(
                        localItemIds,
                        currentRelation?.itemIds,
                        parentFolderId
                    );
                    localItemId && latestItemIdsInForks.push(localItemId);
                }
            }
        });

        return latestItemIdsInForks.reverse();
    }
    return [];
}

export async function getAncestorsOfMessageIncludingCurrentItem(
    parentFolderId: string,
    conversationIdString: string,
    itemId: string
): Promise<string[]> {
    // Add the current itemId to the ancestor ids
    const allAncestorIds: string[] = [itemId];
    const conversationRelationMap = await getConversationItemsRelationMap(conversationIdString);

    if (conversationRelationMap) {
        const message = mailStore.items.get(itemId) as Message;
        if (message?.InternetMessageId) {
            const relationship: ParentChildRelation | undefined = conversationRelationMap.get(
                message.InternetMessageId
            );

            // Get the parent internet message id and start filling the ancestor ids
            let currentImId: string | undefined = relationship?.parentImId;

            while (currentImId) {
                const currentRelation = conversationRelationMap.get(currentImId);

                if (currentRelation) {
                    currentRelation.itemIds.forEach((currentItemId: string) => {
                        // Only return local items
                        const item = mailStore.items.get(currentItemId);
                        if (
                            !parentFolderId ||
                            (item && item.ParentFolderId?.Id == parentFolderId)
                        ) {
                            allAncestorIds.push(currentItemId);
                        }
                    });
                    currentImId = currentRelation.parentImId;
                } else {
                    currentImId = undefined;
                }
            }
        }
    }

    return allAncestorIds;
}

/**
 * Helper function to add nodes to the map and update parent child relationships
 */
function updateConversationRelationMapWithNode(
    conversationItemsRelationMap: Map<string, ParentChildRelation>,
    node: ConversationReadingPaneNode | undefined
): void {
    if (!node || !conversationItemsRelationMap) {
        return;
    }

    const internetMessageId = node.internetMessageId;
    const parentInternetMessageId = node.parentInternetMessageId;

    const item = mailStore.items.get(node.itemIds[0]);
    const itemClass = item?.ItemClass;
    const isOofItem = itemClass ? OOF_ITEM_CLASS_REGEX.test(itemClass) : false;
    // OOF item is a standalone node and should not form part of conversation tree.
    if (!isOofItem && parentInternetMessageId) {
        const parentRelation = conversationItemsRelationMap.get(parentInternetMessageId);
        if (parentRelation) {
            parentRelation.childrenImIds.push(internetMessageId);
        }
    }

    conversationItemsRelationMap.set(internetMessageId, {
        itemIds: node.itemIds,
        parentImId: isOofItem ? undefined : parentInternetMessageId,
        childrenImIds: [],
    });
}

function checkForLocalItemIds(
    localItemIds: string[],
    itemIdsToCheck: string[] | undefined,
    parentFolderId?: string
): string | null {
    if (itemIdsToCheck) {
        // VSO 108934: If mon-conv-useHxForConvListViewLoad is enabled, the app is using Hx resolvers, and the parent folder id is available
        // Use the parent folder id to check for local items, since Hx currently only returns the latest local item
        const shouldCheckParentFolder =
            isFeatureEnabled('mon-conv-useHxForConvListViewLoad') &&
            isHostAppFeatureEnabled('nativeResolvers') &&
            parentFolderId != undefined;
        let isLocalItem;
        for (let i = 0; i < itemIdsToCheck.length; i++) {
            if (shouldCheckParentFolder) {
                const item = mailStore.items.get(itemIdsToCheck[i]);
                isLocalItem = item?.ParentFolderId?.Id == parentFolderId;
            } else {
                isLocalItem = localItemIds.includes(itemIdsToCheck[i]);
            }

            if (isLocalItem) {
                return itemIdsToCheck[i];
            }
        }
    }

    return null;
}
